import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import { currentDomain, currentPath } from "../Index";
import { usePromotionsData } from "../data/PromotionData";
import { createPortal } from "react-dom";
import { useSearchParams, useNavigate } from "react-router-dom";
import JiliQuest from "../snippets/games/JiliQuest";
import ModuleJiliGrandSlot from "./promotion_modules/ModuleJiliGrandSlot";
import useAppPromotionTheme from "../hooks/useAppPromotionTheme";
import LoginGlobal from "../data/LoginGlobal";

export default function JILIQuest({ isLightMode }) {
  const isAppPromo = useAppPromotionTheme(isLightMode);
  const { promotionsData, isPromotionDataReady } = usePromotionsData();
  const navigate = useNavigate();
  const [activeModal, setActiveModal] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isJiliQuestOpen, setIsJiliQuestOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOpenJiliQuest = () => {
    setIsJiliQuestOpen(true);
  };

  useEffect(() => {
    const open = searchParams.get("open");

    if (open === "jili-quest") {
      setIsJiliQuestOpen(true);
    }
  }, [searchParams]);

  const handleCloseJiliQuest = () => {
    setIsJiliQuestOpen(false);

    if (searchParams.get("open") === "jili-quest") {
      const nextParams = new URLSearchParams(searchParams);

      nextParams.delete("open");

      setSearchParams(nextParams, {
        replace: true,
      });
    }
  };

  const modalComponents = {
    "BNG Splash Spin Into Summer": ModuleJiliGrandSlot,
  };

  const data = useMemo(
    () => promotionsData.find((promo) => promo.title === "Jili Quest"),
    [promotionsData],
  );

  const ActiveModal = activeModal ? modalComponents[activeModal] : null;

  useEffect(() => {
    const loggedInState = localStorage.getItem("isLoggedIn") === "true";
    setIsLoggedIn(loggedInState);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 841);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // If the promo page is expired - throws error
  useEffect(() => {
    if (!isPromotionDataReady) return;

    if (!data) {
      navigate("/not-found");
    }
  }, [isPromotionDataReady, data, navigate]);

  if (!isPromotionDataReady || !data) return null;

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Jili Quest: Unlock & Win</title>
        <link rel="icon" href="/images/fav.webp" sizes="32x32" />
        <link rel="canonical" href={`${currentDomain}${currentPath}`} />
      </Helmet>
      <div className="promotion-page-main all-page-main-container-min-height">
        <div className="promotion-page-container main-container-global-adjusted-padding">
          <div className="promotion-tnc-page">
            <div className="promotion-page-breadcrumb">
              <a
                href={
                  isAppPromo
                    ? "/promotion/".replace("/promotion", "/app-promotion")
                    : "/promotion/"
                }
              >
                Promotions
              </a>{" "}
              / <span>{data.title}</span>
            </div>
            <div className="promotion-tnc-banner">
              <img
                src={isMobile ? data.imgSrc : data.bannerSrc}
                alt={`FGH ${data.title}`}
              />
            </div>
            <div className="promotion-tnc-content">
              <div className="promotion-tnc-info">
                <div
                  className={`promotion-indication-message ${data.promotionMessage.class}`}
                >
                  {data.promotionMessage.text}
                </div>
                <h1>Jili Quest: Unlock & Win</h1>
                <div className="promotion-tnc-period">
                  <strong>Promo Period: </strong>
                  <span>Sept 01 (12PM) - Sept 29 (11:59AM), 2026</span>
                </div>
              </div>
              <div className="promotion-tnc-mechanic-content">
                {!isAppPromo && (
                  <div className="promotion-tnc-btn-wrapper">
                    {isLoggedIn ? (
                      <button
                        type="button"
                        onClick={handleOpenJiliQuest}
                        className="promotion-cta-btn"
                      >
                        View Game
                      </button>
                    ) : (
                      <LoginGlobal
                        label="View Game"
                        btnClass="promotion-cta-btn"
                        onlyBtn={true}
                      />
                    )}
                  </div>
                )}
                <strong>Promotion Mechanics</strong>
                <ol>
                  <li>
                    The promotion runs for 4 consecutive weeks, and eligible
                    players may join anytime during the promotion by starting at
                    Stage 1.
                  </li>
                  <li>
                    Complete each stage in order to unlock the next, with 5
                    stages released each week. First stage of the following
                    weeks will open automatically like stage 1.
                    <ul style={{ marginBottom: "0px" }}>
                      Sample:
                      <li>
                        <strong className="highlight">Week 1 starts:</strong>{" "}
                        Stage 1 is available.
                        <br />
                        Complete Stage 1 → Stage 2 unlocks → Stage 3 → Stage 4 →
                        Stage 5.
                      </li>
                      <li>
                        <strong className="highlight">Week 2 starts:</strong>{" "}
                        Stage 6 becomes available
                        <br />
                        Then Stage 7, 8, 9, and 10 unlock one by one as they're
                        completed.
                      </li>
                    </ul>
                  </li>
                  <li>
                    Only turnover generated on the currently unlocked stage's
                    assigned game will count toward stage completion. Excess
                    turnover will not carry over to the next stage.
                  </li>
                  <li>
                    Upon meeting the required turnover for the current stage,
                    the stage will be marked as completed, the corresponding
                    reward will be automatically credited, and the next
                    available stage will unlock.
                  </li>
                  <li>
                    Free Spins and Free Credits will be credited automatically
                    after stage completion. They may be used on all eligible
                    promotion games and and will expire one week after the promo
                    ends.
                  </li>
                  <li>
                    Players may continue progressing until the end of the
                    promotion period. Any incomplete stages remaining after the
                    promotion ends will be forfeited.
                  </li>
                </ol>
                <strong>Terms and Condition</strong>
                <ol>
                  <li>The promotion is open to all eligible FGH players.</li>
                  <li>
                    Eligible players may join the promotion at any time during
                    the promotion period and will begin from Stage 1.
                  </li>
                  <li>
                    Players must complete stages sequentially. Skipping stages
                    is not allowed.{" "}
                  </li>
                  <li>
                    Only turnover generated on the currently unlocked stage's
                    assigned game will count toward stage progression.
                  </li>
                  <li>
                    The first stage of each weekly stage set will become
                    available according to the promotion schedule. Remaining
                    stages within the same week will unlock sequentially after
                    the previous stage has been completed.
                  </li>
                  <li>
                    Rewards will be automatically credited immediately after
                    successful completion of each stage.{" "}
                  </li>
                  <li>
                    Free Spins and Free Credits are non-transferable,
                    non-exchangeable, and cannot be converted into cash unless
                    otherwise stated. Their usage and validity are subject to
                    the platform's promotional rules.
                  </li>
                  <li>
                    The operator reserves the right to verify player
                    eligibility, replace unavailable games with other eligible
                    JILI games, and amend, suspend, or cancel the promotion when
                    necessary.
                  </li>
                  <li>
                    By participating in this promotion, players agree to these
                    Terms & Conditions and the operator's general gaming rules.
                  </li>
                </ol>
                {isAppPromo && (
                  <p style={{ color: "red", fontStyle: "italic" }}>
                    **This game is only available on website.
                  </p>
                )}

                {isJiliQuestOpen &&
                  createPortal(
                    <div
                      className="jili-quest-modal"
                      role="dialog"
                      aria-modal="true"
                    >
                      <JiliQuest onClose={handleCloseJiliQuest} />
                    </div>,
                    document.body,
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {ActiveModal && <ActiveModal closeModal={() => setActiveModal("")} />}
    </>
  );
}
